import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Col, Row } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { ChatIcon, LoaderIcon } from '../Components/Global/Icons';
import { useParams } from 'react-router-dom';
import { FadeAccordion, FadeAccordionItem, FadeAlert, LinkButton } from '../Components/Global/Transitions';
import { getprojectbyidforuser, getQuotePdfInfo, getRetests } from '../Services/PtaasApiService';
import Tests from '../Models/Tests';
import Retest from '../Models/Retest';
import ProjectPreReqs from '../Components/Overview/ProjectPreReqs';
import ProjectTestTypes from '../Components/Overview/ProjectTestTypes';
import QuotationPdf from '../Components/Overview/ProjectQuotationPdf';
import UsersWidget from '../Components/Users/UsersWidget';

// Retest statuses whose quote is open for the client's signature. Anything else — cancelled,
// already in progress, complete — has a quote that can be read but never signed, which the server
// enforces independently in QuotationSignatureService.
const SIGNABLE_RETEST_STATUS = 'Quote Available';

const ORIGINAL_QUOTE_KEY = 'original';

interface QuotePanel {
  key: string;
  title: string;
  // The id the quote is fetched and signed by. A retest quote's id IS its retest id
  // (Quote.Id == RetestRequest.Id, set at create).
  quoteId: string;
  signable: boolean;
  filename: string;
}

// Where a quote sits in the accordion. An unsigned quote outranks a signed one — it is the only
// reason the client is on this page — and among unsigned ones, the quote that can actually be
// signed outranks a quote that never can (a cancelled retest's quote stays unsigned forever, and
// promoting it would bury the actionable quote all over again).
const panelRank = (panel: QuotePanel, signed: boolean): number => {
  if (signed) return 2;
  return panel.signable ? 0 : 1;
};

const ProjectQuotePage: React.FC = () => {
  const { id } = useParams();
  const { instance, accounts } = useMsal();
  const [test, setTest] = useState<Tests>();
  const [retests, setRetests] = useState<Retest[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [chatid, setChatid] = useState('');
  // Which panels the user has opened. A collapsed react-bootstrap panel still mounts its children,
  // so without driving the accordion the page would fetch every quote's PDF at once on load — one
  // request per retest. Panels are kept in the set once opened so re-collapsing does not throw the
  // fetched PDF away. Both start empty: nothing is rendered while loading, and which panel opens
  // is decided once the signature of every quote is known.
  const [openedKeys, setOpenedKeys] = useState<string[]>([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  // Signature state per quote id, from quote-pdf-info — the same flag the panel body reads back.
  // Read once, on arrival: it decides the accordion's order, and re-reading it after the client
  // signs would drop the panel they are looking at halfway down the page.
  const [signedByQuoteId, setSignedByQuoteId] = useState<Record<string, boolean>>({});
  const signaturesLoaded = useRef(false);
  const arrivalPanelChosen = useRef(false);

  // The signature of every quote on the page, asked for in one go. quote-pdf-info carries isSigned
  // without the PDF itself, so this stays cheap enough to run for all of them up front — the
  // download is the expensive half and still waits until a panel is opened.
  const readSignatures = useCallback(async (loadedRetests: Retest[]): Promise<Record<string, boolean>> => {
    const quoteIds = [id ?? '', ...loadedRetests.filter((retest) => !!retest.quoteId).map((retest) => retest.id)];
    const entries = await Promise.all(quoteIds.map(async (quoteId): Promise<[string, boolean]> => {
      try {
        const info = await getQuotePdfInfo(quoteId, instance, accounts[0]);
        return [quoteId, info?.isSigned ?? true];
      } catch {
        // A quote whose state cannot be read — 404 for one that was never generated, say — counts
        // as signed: it must not take the top slot and open a panel that can only show an error.
        return [quoteId, true];
      }
    }));
    return Object.fromEntries(entries);
  }, [accounts, id, instance]);

  const fetchQuote = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const returnedTest = await getprojectbyidforuser(instance, accounts[0], id);
      setTest(returnedTest);
      setChatid(returnedTest?.ticketno?.replace(/\D/g, ''));

      // Every retest, not just the open one: each has its own quote and the client is entitled to
      // read back what they signed. A failure here must not block the original quote from
      // rendering — that is the one quote every project has.
      let loadedRetests: Retest[] = [];
      try {
        loadedRetests = await getRetests(instance, accounts[0], id) ?? [];
      } catch {
        loadedRetests = [];
      }
      setRetests(loadedRetests);

      if (!signaturesLoaded.current) {
        setSignedByQuoteId(await readSignatures(loadedRetests));
        signaturesLoaded.current = true;
      }
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [accounts, instance, id, readSignatures]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const panels = useMemo<QuotePanel[]>(() => {
    const original: QuotePanel = {
      key: ORIGINAL_QUOTE_KEY,
      title: 'Original Quote',
      quoteId: id ?? '',
      signable: true,
      filename: 'Quotation',
    };

    // Only retests that actually produced a quote. A retest where every finding was risk accepted
    // lands Complete immediately with no tester work to price, so it has no quoteId and no quote
    // to show (see RetestService.CreateRetestAsync's zero-retest branch).
    const retestPanels = retests
      .filter((retest) => !!retest.quoteId)
      .slice()
      .sort((a, b) => a.retestNumber - b.retestNumber)
      .map<QuotePanel>((retest) => ({
        key: retest.id,
        title: `Retest ${retest.retestNumber}`,
        quoteId: retest.id,
        signable: retest.status === SIGNABLE_RETEST_STATUS,
        filename: `Quotation-Retest-${retest.retestNumber}`,
      }));

    // Unsigned quotes float to the top. Array.sort is stable, so within a rank the natural
    // reading order survives: the original quote, then the retests in retest order.
    return [original, ...retestPanels]
      .sort((a, b) => panelRank(a, signedByQuoteId[a.quoteId] ?? true) - panelRank(b, signedByQuoteId[b.quoteId] ?? true));
  }, [id, retests, signedByQuoteId]);

  // Exactly one panel is open on arrival, and it is the top one — the unsigned quote when there is
  // one, the original quote otherwise. Every other panel stays collapsed, so no quote the client
  // has already dealt with downloads its PDF. Runs once: the refetch that follows signing must not
  // collapse whatever the client has since opened.
  useEffect(() => {
    if (loading || arrivalPanelChosen.current) return;
    const [first] = panels;
    if (!first) return;
    arrivalPanelChosen.current = true;
    setOpenedKeys([first.key]);
    setActiveKeys([first.key]);
  }, [loading, panels]);

  const handleSelect = (key: string | string[] | null | undefined) => {
    const next = Array.isArray(key) ? key : key ? [key] : [];
    setActiveKeys(next);
    setOpenedKeys((prev) => Array.from(new Set([...prev, ...next])));
  };

  if (loading) return <LoaderIcon />;

  return (
    <>
      <Row className='mb-4 pb-3'>
        <Col sm={8} as='h1'>{test?.projectName ?? ''}</Col>
        <Col sm={4} className='text-end'>
          <LinkButton variant='secondary' to={`/messaging?id=${chatid}`}><ChatIcon />Messaging</LinkButton>
        </Col>
      </Row>
      <Row>
        <Col xl={8}>
          <FadeAlert show={fetchError} dismissFunction={() => setFetchError(false)} />
          {/* One panel per quote. There is no Accept button anywhere on this page: signing a quote
              IS accepting it, for a retest quote exactly as for the original one. */}
          <FadeAccordion alwaysOpen activeKey={activeKeys} onSelect={handleSelect}>
            {panels.map((panel) => (
              <FadeAccordionItem key={panel.key} eventKey={panel.key}>
                <Accordion.Button as='h3'>{panel.title}</Accordion.Button>
                <Accordion.Body>
                  {openedKeys.includes(panel.key) ? (
                    <QuotationPdf
                      testid={panel.quoteId}
                      signable={panel.signable}
                      filename={panel.filename}
                      onSigned={fetchQuote}
                    />
                  ) : null}
                </Accordion.Body>
              </FadeAccordionItem>
            ))}
          </FadeAccordion>
        </Col>
        <Col md={10} lg={8} xl={4}>
          <ProjectPreReqs assignments={test?.assignment ?? []} test={test} />
          <ProjectTestTypes test={test} testTypes={test?.testType ?? []} isDisabled OnProjectTestTypeChange={fetchQuote} />
          <UsersWidget />
        </Col>
      </Row>
    </>
  );
};

export default ProjectQuotePage;
